import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, Route, HashRouter, Routes, useLocation, useNavigate } from 'react-router-dom';

import { fetchSetupStatus } from './api/auth';
import { setUnauthorizedHandler } from './api/client';
import { AppLayout } from './components/layout/AppLayout';
import { ToastProvider } from './components/ui/toast';
import { useAuthBootstrap } from './hooks/use-auth-bootstrap';
import { useSession } from './hooks/use-session';
import { captureExtChallenge } from './lib/ext-challenge';
import { rememberReturnTo } from './lib/return-to';
import { AccountPage } from './routes/account/AccountPage';
import { ForcePasswordChangePage } from './routes/account/ForcePasswordChangePage';
import { SetupPage } from './routes/setup/SetupPage';
import { AgentsPage } from './routes/agents/AgentsPage';
import { AnalyticsPage } from './routes/analytics/AnalyticsPage';
import { ChannelsPage } from './routes/channels/ChannelsPage';
import { ContactsPage } from './routes/contacts/ContactsPage';
import { DevTelegramPage } from './routes/dev/DevTelegramPage';
import { DevVkPage } from './routes/dev/DevVkPage';
import { DialogPage } from './routes/dialog/DialogPage';
import { ExtensionPage } from './routes/extension/ExtensionPage';
import { InboxPage } from './routes/inbox/InboxPage';
import { KanbanPage } from './routes/kanban/KanbanPage';
import { LoginPage } from './routes/login/LoginPage';
import { SettingsPage } from './routes/settings/SettingsPage';
import { StubPage } from './routes/stub/StubPage';
import { isDevToolsEnabled, NAV_ITEMS } from './components/layout/nav';
import { wsClient } from './ws/client';
import { wireWsToQueryClient } from './ws/query-bridge';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 5_000 } },
});

/** Paths that are never a "return here after login" destination. */
const AUTH_PATHS = new Set(['/login', '/setup']);

/**
 * A 401 anywhere sends the user to /login — remembering where they were, so
 * signing back in returns them to the same page (query string included).
 */
function UnauthorizedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const here = useRef(location);
  here.current = location;
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const { pathname, search } = here.current;
      if (AUTH_PATHS.has(pathname)) return;
      rememberReturnTo(pathname + search);
      void navigate('/login', { replace: true });
    });
  }, [navigate]);
  return null;
}

/**
 * The extension's connect link carries `?ext_challenge=…`. It is remembered
 * for the tab the moment any route sees it — before a login redirect can drop
 * the query string — so «Расширение» still has it afterwards.
 */
function ExtChallengeCapture() {
  const { search } = useLocation();
  useMemo(() => captureExtChallenge(search), [search]);
  return null;
}

type SetupState = 'loading' | 'needs_setup' | 'configured';

/**
 * First-run gate: while the instance has no users every route leads to
 * /setup (keeping `?token=…`); once it is configured /setup leads to /login.
 * If the status cannot be fetched the app behaves as configured — the login
 * page is the safe default.
 */
function SetupGate({ children }: { children: (markConfigured: () => void) => ReactNode }) {
  const [state, setState] = useState<SetupState>('loading');
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    fetchSetupStatus()
      .then((res) => {
        if (!cancelled) setState(res.needs_setup ? 'needs_setup' : 'configured');
      })
      .catch(() => {
        if (!cancelled) setState('configured');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return <div className="flex h-full items-center justify-center text-sm text-muted-fg">Загрузка…</div>;
  }
  if (state === 'needs_setup' && location.pathname !== '/setup') {
    return <Navigate to={{ pathname: '/setup', search: location.search }} replace />;
  }
  if (state === 'configured' && location.pathname === '/setup') return <Navigate to="/login" replace />;
  return <>{children(() => setState('configured'))}</>;
}

function ProtectedShell() {
  const status = useAuthBootstrap();
  const location = useLocation();
  const { me } = useSession();
  const mustChangePassword = me?.mustChangePassword === true;
  const ready = status === 'authenticated' && !mustChangePassword;

  useEffect(() => {
    if (!ready) return;
    wsClient.connect();
    const unwire = wireWsToQueryClient(queryClient);
    return () => {
      unwire();
      wsClient.close();
    };
  }, [ready]);

  if (status === 'loading') {
    return <div className="flex h-full items-center justify-center text-sm text-muted-fg">Загрузка…</div>;
  }
  if (status === 'anonymous') {
    // Remember the page the visitor asked for; LoginPage returns there after sign-in.
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  // A temporary password opens nothing else — the server enforces the same rule.
  if (mustChangePassword) return <ForcePasswordChangePage />;

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to="/inbox" replace />} />
        <Route path="inbox/:dialogId?" element={<InboxPage />} />
        <Route path="dialogs/:id" element={<DialogPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="kanban" element={<KanbanPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="extension" element={<ExtensionPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        {isDevToolsEnabled() && (
          <>
            <Route path="dev/vk" element={<DevVkPage />} />
            <Route path="dev/telegram" element={<DevTelegramPage />} />
          </>
        )}
        {NAV_ITEMS.filter((n) => !n.implemented).map((n) => (
          <Route key={n.to} path={n.to.slice(1)} element={<StubPage title={n.label} />} />
        ))}
        <Route path="*" element={<Navigate to="/inbox" replace />} />
      </Route>
    </Routes>
  );
}

/** Everything below the router — exported so tests can mount it in a MemoryRouter. */
export function AppRoutes() {
  return (
    <>
      <UnauthorizedRedirect />
      <ExtChallengeCapture />
      <SetupGate>
        {(markConfigured) => (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage onDone={markConfigured} />} />
            <Route path="/*" element={<ProtectedShell />} />
          </Routes>
        )}
      </SetupGate>
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
